import React from 'react';
import { ProductLookupResult, VariantWithInventory } from '../types';
import { LocationInventory } from './LocationInventory';

interface ProductResultProps {
  result: ProductLookupResult;
  currency: string;
  onVariantSelect: (sku: string) => void;
}

function formatPrice(price?: number | null, currency = 'USD'): string {
  if (price == null) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(price);
}

function totalStock(inventory: VariantWithInventory['inventory']): number {
  return inventory.reduce((sum, loc) => sum + loc.available, 0);
}

// Map of common color names to CSS color values for swatches
const COLOR_MAP: Record<string, string> = {
  black: '#111',
  white: '#f7f7f7',
  red: '#e53e3e',
  green: '#38a169',
  blue: '#3182ce',
  navy: '#1a365d',
  yellow: '#d69e2e',
  orange: '#dd6b20',
  purple: '#805ad5',
  pink: '#d53f8c',
  grey: '#718096',
  gray: '#718096',
  brown: '#744210',
  beige: '#c9a96e',
  cream: '#fefcbf',
  ivory: '#f5f0e8',
  khaki: '#a0856c',
  coral: '#fc8181',
  teal: '#2c7a7b',
  mint: '#68d391',
  lavender: '#b794f4',
  maroon: '#702459',
  olive: '#556b2f',
  gold: '#c6a700',
  silver: '#a0aec0',
  rose: '#fb7185',
  indigo: '#4c51bf',
  cyan: '#00b5d8',
  lime: '#84cc16',
};

function getColorSwatch(value: string): string | null {
  const lower = value.toLowerCase().trim();
  // Direct match
  if (COLOR_MAP[lower]) return COLOR_MAP[lower];
  // Partial match — "Dark Navy" → navy
  for (const [key, hex] of Object.entries(COLOR_MAP)) {
    if (lower.includes(key)) return hex;
  }
  return null;
}

function ColorSwatch({ color }: { color: string }) {
  const isLight = ['white', 'cream', 'ivory', 'beige', 'yellow', 'lime', 'mint'].some((c) =>
    color.toLowerCase().includes(c)
  );
  return (
    <span
      style={{
        display: 'inline-block',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: getColorSwatch(color) ?? '#718096',
        border: isLight ? '1.5px solid #4a5568' : '1.5px solid rgba(255,255,255,0.15)',
        flexShrink: 0,
        verticalAlign: 'middle',
      }}
    />
  );
}

// A compact "size/option" chip — used for option2/option3 within a group
function OptionChip({
  label,
  stock,
  price,
  currency,
  clickable,
  onSelect,
}: {
  label: string;
  stock: number;
  price?: number | null;
  currency: string;
  clickable: boolean;
  onSelect?: () => void;
}) {
  const inStock = stock > 0;
  return (
    <button
      onClick={clickable ? onSelect : undefined}
      disabled={!clickable}
      title={clickable ? `Look up ${label}` : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
        padding: '10px 14px',
        borderRadius: '8px',
        border: `1.5px solid ${inStock ? '#2f855a' : '#4a5568'}`,
        background: inStock ? 'rgba(47,133,90,0.12)' : 'rgba(45,55,72,0.5)',
        cursor: clickable ? 'pointer' : 'default',
        minWidth: '56px',
        transition: 'filter 0.15s, border-color 0.15s',
        opacity: inStock ? 1 : 0.55,
      }}
      onMouseEnter={clickable ? (e) => { (e.currentTarget as HTMLButtonElement).style.filter = 'brightness(1.25)'; } : undefined}
      onMouseLeave={clickable ? (e) => { (e.currentTarget as HTMLButtonElement).style.filter = ''; } : undefined}
    >
      <span style={{ fontSize: '15px', fontWeight: 700, color: inStock ? '#e2e8f0' : '#718096', whiteSpace: 'nowrap' }}>
        {label}
      </span>
      <span style={{
        fontSize: '11px',
        fontWeight: 600,
        color: inStock ? '#68d391' : '#718096',
        whiteSpace: 'nowrap',
      }}>
        {inStock ? `${stock} left` : 'Out'}
      </span>
      {price != null && (
        <span style={{ fontSize: '11px', color: '#a0aec0', whiteSpace: 'nowrap' }}>
          {formatPrice(price, currency)}
        </span>
      )}
    </button>
  );
}

// A card grouping variants that share the same primary option (option1)
function OptionGroup({
  groupLabel,
  variants,
  currency,
  onVariantSelect,
}: {
  groupLabel: string | null;
  variants: VariantWithInventory[];
  currency: string;
  onVariantSelect: (sku: string) => void;
}) {
  const swatch = groupLabel ? getColorSwatch(groupLabel) : null;
  const groupStock = variants.reduce((sum, v) => sum + totalStock(v.inventory), 0);
  const groupInStock = groupStock > 0;

  return (
    <div style={{
      background: '#1a1a1a',
      border: `1.5px solid ${groupInStock ? '#2d3748' : '#2d2d2d'}`,
      borderRadius: '12px',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      flex: '1 1 220px',
      maxWidth: '340px',
    }}>
      {/* Group header */}
      {groupLabel && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {swatch && <ColorSwatch color={groupLabel} />}
          <span style={{ fontSize: '15px', fontWeight: 700, color: '#e2e8f0' }}>{groupLabel}</span>
          <span style={{
            marginLeft: 'auto',
            fontSize: '11px',
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: '20px',
            background: groupInStock ? 'rgba(47,133,90,0.2)' : 'rgba(116,42,42,0.2)',
            color: groupInStock ? '#68d391' : '#fc8181',
          }}>
            {groupInStock ? `${groupStock} total` : 'Out of stock'}
          </span>
        </div>
      )}

      {/* Option chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
        {variants.map((v) => {
          const chipLabel = [v.option2, v.option3].filter(Boolean).join(' / ') || v.title || 'Select';
          const stock = totalStock(v.inventory);
          return (
            <OptionChip
              key={v.variantId}
              label={chipLabel}
              stock={stock}
              price={v.price}
              currency={currency}
              clickable={Boolean(v.sku)}
              onSelect={v.sku ? () => onVariantSelect(v.sku!) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

export function ProductResult({ result, currency, onVariantSelect }: ProductResultProps) {
  const { product, scannedVariant, siblings, inventory } = result;

  const scannedLabel = [scannedVariant.option1, scannedVariant.option2, scannedVariant.option3]
    .filter(Boolean)
    .join(' / ') || scannedVariant.title || 'This variant';

  // Group siblings by option1 (primary option — typically color)
  const hasOption1 = siblings.some((s) => s.option1);
  const hasOption2 = siblings.some((s) => s.option2);

  // If all variants have the same option1 (or no option1), treat as a flat list
  const uniqueOption1 = [...new Set(siblings.map((s) => s.option1 ?? null))];
  const useGroups = hasOption1 && hasOption2 && uniqueOption1.length > 1;

  const groups: { label: string | null; variants: VariantWithInventory[] }[] = [];
  if (useGroups) {
    for (const label of uniqueOption1) {
      groups.push({
        label,
        variants: siblings.filter((s) => (s.option1 ?? null) === label),
      });
    }
  } else {
    // Flat: single group with no header, or option1 as the chip label
    groups.push({ label: null, variants: siblings });
  }

  return (
    <div style={styles.container}>
      {/* Product header */}
      <div style={styles.productHeader}>
        {product.imageUrl && (
          <img
            src={product.imageUrl}
            alt={product.title}
            style={styles.productImage}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <div style={styles.productInfo}>
          <div style={styles.productMeta}>
            {product.vendor && (
              <span style={styles.vendor}>{product.vendor}</span>
            )}
            {product.productType && (
              <span style={styles.productType}>{product.productType}</span>
            )}
          </div>
          <h1 style={styles.productTitle}>{product.title}</h1>
          <div style={styles.scannedVariantRow}>
            <span style={styles.scannedLabel}>You scanned:</span>
            <span style={styles.scannedValue}>{scannedLabel}</span>
            {scannedVariant.sku && (
              <span style={styles.scannedSku}>{scannedVariant.sku}</span>
            )}
            {scannedVariant.price != null && (
              <span style={styles.scannedPrice}>{formatPrice(scannedVariant.price, currency)}</span>
            )}
          </div>
        </div>
      </div>

      <div style={styles.divider} />

      {/* Stock for scanned variant at all locations */}
      <section style={styles.section}>
        <LocationInventory
          inventory={inventory}
          title="Stock at Each Location for This Variant"
        />
      </section>

      {/* Sibling variants */}
      {siblings.length > 0 && (
        <>
          <div style={styles.divider} />
          <section style={styles.section}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '4px' }}>
              <h2 style={styles.sectionHeading}>Other Variants</h2>
              <span style={{ fontSize: '13px', color: '#4a5568' }}>{siblings.length} variant{siblings.length !== 1 ? 's' : ''}</span>
            </div>
            <p style={styles.sectionSubheading}>
              Tap a variant to look up its stock at each location.
            </p>

            {useGroups ? (
              // Grouped layout: one card per primary option (color)
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                {groups.map((g) => (
                  <OptionGroup
                    key={g.label ?? '__flat__'}
                    groupLabel={g.label}
                    variants={g.variants}
                    currency={currency}
                    onVariantSelect={onVariantSelect}
                  />
                ))}
              </div>
            ) : (
              // Flat layout: single row of chips
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {siblings.map((sibling) => {
                  const chipLabel = [sibling.option1, sibling.option2, sibling.option3]
                    .filter(Boolean)
                    .join(' / ') || sibling.title || 'Variant';
                  const stock = totalStock(sibling.inventory);
                  return (
                    <OptionChip
                      key={sibling.variantId}
                      label={chipLabel}
                      stock={stock}
                      price={sibling.price}
                      currency={currency}
                      clickable={Boolean(sibling.sku)}
                      onSelect={sibling.sku ? () => onVariantSelect(sibling.sku!) : undefined}
                    />
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100%',
    maxWidth: '960px',
    margin: '0 auto',
    padding: '0 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
  },
  productHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '24px',
    padding: '8px 0 24px',
  },
  productImage: {
    width: '100px',
    height: '100px',
    objectFit: 'cover',
    borderRadius: '10px',
    flexShrink: 0,
    border: '1px solid #2d3748',
  },
  productInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  productMeta: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  vendor: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#3182ce',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    background: 'rgba(49,130,206,0.12)',
    padding: '3px 10px',
    borderRadius: '20px',
  },
  productType: {
    fontSize: '13px',
    color: '#718096',
    background: '#2d3748',
    padding: '3px 10px',
    borderRadius: '20px',
  },
  productTitle: {
    margin: 0,
    fontSize: '32px',
    fontWeight: 800,
    color: '#f7fafc',
    lineHeight: 1.15,
    letterSpacing: '-0.01em',
  },
  scannedVariantRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    marginTop: '4px',
  },
  scannedLabel: {
    fontSize: '14px',
    color: '#718096',
    fontWeight: 500,
  },
  scannedValue: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#68d391',
  },
  scannedSku: {
    fontSize: '14px',
    color: '#718096',
    fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
    letterSpacing: '0.04em',
  },
  scannedPrice: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#a0aec0',
  },
  divider: {
    height: '1px',
    background: '#2d3748',
    margin: '16px 0',
  },
  section: {
    paddingBottom: '8px',
  },
  sectionHeading: {
    margin: '0 0 6px 0',
    fontSize: '20px',
    fontWeight: 700,
    color: '#e2e8f0',
  },
  sectionSubheading: {
    margin: '0 0 16px 0',
    fontSize: '14px',
    color: '#718096',
  },
};
